import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../api';
import { useToast } from '../../components/Toast';
import { SIDEBAR_WIDTH } from '../../components/AdminSidebar';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function confPill(conf) {
  if (conf == null) return { bg: 'rgba(71,85,105,0.3)', color: '#475569', text: '—' };
  const pct = Math.round(conf * 100);
  if (conf >= 0.7) return { bg: 'rgba(74,222,128,0.12)', color: '#4ade80', text: `${pct}%` };
  if (conf >= 0.4) return { bg: 'rgba(251,191,36,0.12)', color: '#fbbf24', text: `${pct}%` };
  return { bg: 'rgba(248,113,113,0.12)', color: '#f87171', text: `${pct}%` };
}

const STATUS_C = {
  discovered:     { bg: 'rgba(96,165,250,0.12)',  color: '#60a5fa' },
  failed:         { bg: 'rgba(248,113,113,0.12)', color: '#f87171' },
  profiled:       { bg: 'rgba(192,132,252,0.12)', color: '#c084fc' },
  matched:        { bg: 'rgba(52,211,153,0.12)',  color: '#34d399' },
  flagged:        { bg: 'rgba(251,146,60,0.12)',  color: '#fb923c' },
  pending_review: { bg: 'rgba(251,191,36,0.12)',  color: '#fbbf24' },
  approved:       { bg: 'rgba(74,222,128,0.12)',  color: '#4ade80' },
  skipped:        { bg: 'rgba(100,116,139,0.15)', color: '#64748b' },
  contacted:      { bg: 'rgba(110,231,183,0.12)', color: '#6ee7b7' },
};

function statusBadge(status) {
  const c = STATUS_C[status] || { bg: 'rgba(100,116,139,0.15)', color: '#94a3b8' };
  return {
    display: 'inline-block', fontSize: 11, fontWeight: 600,
    padding: '2px 8px', borderRadius: 20,
    background: c.bg, color: c.color,
  };
}

function scoreColor(n) {
  if (n >= 70) return '#4ade80';
  if (n >= 40) return '#fbbf24';
  return '#f87171';
}

const s = {
  page: {
    maxWidth: 1100, margin: '0 auto',
    padding: '32px 28px 100px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },

  // ── Top bar ───────────────────────────────────────────────────────────────
  titleRow: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 16 },
  heading: { fontSize: 22, fontWeight: 700, color: '#f1f5f9', margin: 0 },
  statsRow: { display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1 },
  statPill: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: '#111827', border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 8, padding: '5px 12px',
  },
  statLabel: { fontSize: 11, color: '#475569', fontWeight: 500 },
  statVal:   { fontSize: 13, color: '#e2e8f0', fontWeight: 600 },

  // ── Action row ────────────────────────────────────────────────────────────
  actionRow: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 },
  btn: (v, disabled) => {
    const base = { padding: '7px 14px', borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1 };
    if (v === 'primary') return { ...base, background: '#1e3a5f', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.25)' };
    if (v === 'ghost')   return { ...base, background: 'transparent', color: '#64748b', border: '1px solid rgba(255,255,255,0.08)' };
    if (v === 'green')   return { ...base, background: '#052e1c', color: '#4ade80', border: '1px solid rgba(74,222,128,0.25)' };
    if (v === 'gray')    return { ...base, background: '#1a2438', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.08)' };
    return base;
  },

  // ── Inline forms ──────────────────────────────────────────────────────────
  inlineForm: {
    background: '#111827', border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 10, padding: '16px 20px', marginBottom: 14,
    display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap',
  },
  formGroup: { display: 'flex', flexDirection: 'column', gap: 5 },
  lbl: { fontSize: 11, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' },
  input: {
    background: '#0f1a2e', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 7, color: '#f1f5f9', fontSize: 13,
    padding: '7px 12px', outline: 'none', minWidth: 240,
  },
  numInput: {
    background: '#0f1a2e', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 7, color: '#f1f5f9', fontSize: 13,
    padding: '7px 10px', outline: 'none', width: 70,
  },
  checkRow: { display: 'flex', alignItems: 'center', gap: 7, paddingBottom: 1 },
  checkLbl: { fontSize: 13, color: '#94a3b8' },

  // ── Table ─────────────────────────────────────────────────────────────────
  table: {
    width: '100%', borderCollapse: 'collapse',
    background: '#111827', borderRadius: 10,
    overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)',
  },
  th: {
    fontSize: 11, fontWeight: 700, color: '#475569',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    padding: '10px 14px', textAlign: 'left',
    background: '#0c1322', borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  td: { fontSize: 13, color: '#94a3b8', padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'middle' },
  tdPrimary: { fontSize: 13, color: '#e2e8f0', fontWeight: 500, padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'middle' },
  reviewBtn: {
    background: 'rgba(96,165,250,0.1)', color: '#60a5fa',
    border: '1px solid rgba(96,165,250,0.2)', borderRadius: 6,
    fontSize: 12, fontWeight: 600, padding: '4px 10px', cursor: 'pointer',
  },
  emptyRow: { textAlign: 'center', padding: '48px', color: '#334155', fontSize: 14 },

  // ── Detail: back ──────────────────────────────────────────────────────────
  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    color: '#60a5fa', fontSize: 13, fontWeight: 500,
    cursor: 'pointer', background: 'none', border: 'none', padding: '4px 0', marginBottom: 22,
  },

  // ── Detail: layout ────────────────────────────────────────────────────────
  detailGrid: { display: 'grid', gridTemplateColumns: '60fr 40fr', gap: 20 },
  card: {
    background: '#111827', border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12, padding: '20px 22px', marginBottom: 16,
  },
  cardLabel: {
    fontSize: 11, fontWeight: 700, color: '#334155',
    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14,
  },
  companyName: { fontSize: 21, fontWeight: 700, color: '#f1f5f9', marginBottom: 3 },
  domainText:  { fontSize: 13, color: '#475569', marginBottom: 10 },
  fieldsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px', marginBottom: 14 },
  fieldLbl: { fontSize: 11, color: '#334155', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 },
  fieldVal: { fontSize: 13, color: '#cbd5e1' },
  tagsWrap: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 3 },
  tag: {
    background: 'rgba(96,165,250,0.08)', color: '#93c5fd',
    border: '1px solid rgba(96,165,250,0.15)',
    borderRadius: 5, fontSize: 11, fontWeight: 500, padding: '2px 7px',
  },
  subjectInput: {
    width: '100%', boxSizing: 'border-box',
    background: '#0f1a2e', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 7, color: '#f1f5f9', fontSize: 13,
    padding: '7px 12px', outline: 'none', fontFamily: 'inherit', marginBottom: 10,
  },
  bodyTextarea: {
    width: '100%', boxSizing: 'border-box', resize: 'vertical',
    background: '#0f1a2e', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 7, color: '#f1f5f9', fontSize: 13,
    padding: '8px 12px', outline: 'none', fontFamily: 'inherit', lineHeight: 1.55,
  },

  // ── Detail: match cards ───────────────────────────────────────────────────
  matchCard: {
    background: '#0c1322', border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 8, padding: '12px 14px', marginBottom: 8,
  },
  matchTitle:     { fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 5 },
  matchMeta:      { display: 'flex', gap: 14, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' },
  matchMetaItem:  { fontSize: 12, color: '#475569' },
  matchReasoning: { fontSize: 12, color: '#334155', fontStyle: 'italic', lineHeight: 1.4 },

  // ── Action bar ────────────────────────────────────────────────────────────
  actionBar: {
    position: 'fixed', bottom: 0, left: SIDEBAR_WIDTH, right: 0,
    background: '#0c1322', borderTop: '1px solid rgba(255,255,255,0.08)',
    padding: '12px 32px', display: 'flex', alignItems: 'center', gap: 12, zIndex: 100,
  },
  outreachStatusText: { fontSize: 13, color: '#475569', marginLeft: 'auto' },
};

export default function AdminProspector() {
  const [prospects, setProspects]       = useState([]);
  const [stats, setStats]               = useState(null);
  const [selectedId, setSelectedId]     = useState(null);
  const [detail, setDetail]             = useState(null);
  const [loading, setLoading]           = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [runForm, setRunForm]           = useState({ open: false, domain: '', force: false });
  const [discoverForm, setDiscoverForm] = useState({ open: false, query: '', limit: 20 });
  const [emailDraft, setEmailDraft]     = useState({ subject: '', body: '' });
  const [saving, setSaving]             = useState(false);
  const [acting, setActing]             = useState(false);
  const showToast = useToast();

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiFetch('/admin/prospector/prospects?limit=200'),
      apiFetch('/admin/prospector/stats'),
    ])
      .then(([list, st]) => { setProspects(list || []); setStats(st); })
      .catch(() => showToast('Failed to load prospects', 'error'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  function handleSelectProspect(id) {
    setSelectedId(id);
    setDetailLoading(true);
    apiFetch(`/admin/prospector/prospects/${id}`)
      .then((data) => {
        setDetail(data);
        setEmailDraft({
          subject: data.outreach?.email_subject || '',
          body:    data.outreach?.email_body    || '',
        });
      })
      .catch(() => showToast('Failed to load prospect detail', 'error'))
      .finally(() => setDetailLoading(false));
  }

  function handleBack() {
    setSelectedId(null);
    setDetail(null);
  }

  async function submitRun(e) {
    e.preventDefault();
    try {
      const res = await apiFetch('/admin/prospector/run', {
        method: 'POST',
        body: JSON.stringify({ domain: runForm.domain.trim(), force: runForm.force }),
      });
      showToast(res.message || 'Pipeline started', 'success');
      setRunForm({ open: false, domain: '', force: false });
    } catch (err) {
      showToast('Error: ' + err.message, 'error');
    }
  }

  async function submitDiscover(e) {
    e.preventDefault();
    try {
      const res = await apiFetch('/admin/prospector/discover', {
        method: 'POST',
        body: JSON.stringify({ query: discoverForm.query.trim(), limit: Number(discoverForm.limit) }),
      });
      showToast(res.message || 'Discovery started', 'success');
      setDiscoverForm({ open: false, query: '', limit: 20 });
    } catch (err) {
      showToast('Error: ' + err.message, 'error');
    }
  }

  async function handleSaveDraft() {
    setSaving(true);
    try {
      await apiFetch(`/admin/prospector/prospects/${selectedId}/email`, {
        method: 'POST',
        body: JSON.stringify({ subject: emailDraft.subject, body: emailDraft.body }),
      });
      showToast('Draft saved', 'success');
    } catch (err) {
      showToast('Error: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove() {
    setActing(true);
    try {
      const res = await apiFetch(`/admin/prospector/prospects/${selectedId}/approve`, { method: 'POST' });
      showToast(`Queued for ${res.scheduled_date}`, 'success');
      handleBack();
      loadAll();
    } catch (err) {
      showToast('Error: ' + err.message, 'error');
    } finally {
      setActing(false);
    }
  }

  async function handleSkip() {
    setActing(true);
    try {
      await apiFetch(`/admin/prospector/prospects/${selectedId}/skip`, { method: 'POST' });
      showToast('Prospect skipped', 'info');
      handleBack();
      loadAll();
    } catch (err) {
      showToast('Error: ' + err.message, 'error');
    } finally {
      setActing(false);
    }
  }

  // ── Detail view ────────────────────────────────────────────────────────────
  if (selectedId) {
    const pj = detail?.profile?.profile_json || {};
    const outreach = detail?.outreach;
    const matches  = detail?.top_matches || [];

    return (
      <div style={s.page}>
        <button style={s.backBtn} onClick={handleBack}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
          Back to list
        </button>

        {detailLoading || !detail ? (
          <p style={{ color: '#334155', fontSize: 14 }}>Loading…</p>
        ) : (
          <div style={s.detailGrid}>
            {/* ── Left column ─────────────────────────────────────────── */}
            <div>
              {/* Header */}
              <div style={s.card}>
                <div style={s.companyName}>{detail.company_name || detail.domain}</div>
                <div style={s.domainText}>{detail.domain}</div>
                <span style={statusBadge(detail.status)}>{detail.status?.replace('_', ' ')}</span>
              </div>

              {/* Profile fields */}
              <div style={s.card}>
                <div style={s.cardLabel}>Profile</div>
                <div style={s.fieldsGrid}>
                  <div>
                    <div style={s.fieldLbl}>Country</div>
                    <div style={s.fieldVal}>{pj.country || '—'}</div>
                  </div>
                  <div>
                    <div style={s.fieldLbl}>Sector</div>
                    <div style={s.fieldVal}>{pj.sector || '—'}</div>
                  </div>
                  <div>
                    <div style={s.fieldLbl}>Confidence</div>
                    {(() => {
                      const c = confPill(detail.profile?.confidence);
                      return (
                        <span style={{ display: 'inline-block', background: c.bg, color: c.color, fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 6 }}>
                          {c.text}
                        </span>
                      );
                    })()}
                  </div>
                  <div>
                    <div style={s.fieldLbl}>Procurement types</div>
                    <div style={s.fieldVal}>{(pj.procurement_types || []).join(', ') || '—'}</div>
                  </div>
                </div>

                {(pj.specialisations || []).length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={s.fieldLbl}>Specialisations</div>
                    <div style={s.tagsWrap}>
                      {pj.specialisations.map((t, i) => <span key={i} style={s.tag}>{t}</span>)}
                    </div>
                  </div>
                )}

                {(pj.cpv_interests || []).length > 0 && (
                  <div>
                    <div style={s.fieldLbl}>CPV interests</div>
                    <div style={s.tagsWrap}>
                      {pj.cpv_interests.map((t, i) => <span key={i} style={s.tag}>{t}</span>)}
                    </div>
                  </div>
                )}
              </div>

              {/* Contact */}
              {(outreach?.contact_name || outreach?.contact_email) && (
                <div style={s.card}>
                  <div style={s.cardLabel}>Contact</div>
                  {outreach.contact_name  && <div style={{ ...s.fieldVal, marginBottom: 4 }}>{outreach.contact_name}</div>}
                  {outreach.contact_email && <div style={{ fontSize: 13, color: '#60a5fa' }}>{outreach.contact_email}</div>}
                </div>
              )}

              {/* Email draft */}
              <div style={s.card}>
                <div style={s.cardLabel}>Email Draft</div>
                <input
                  style={s.subjectInput}
                  placeholder="Subject line…"
                  value={emailDraft.subject}
                  onChange={(e) => setEmailDraft((d) => ({ ...d, subject: e.target.value }))}
                />
                <textarea
                  rows={8}
                  style={s.bodyTextarea}
                  placeholder="Email body…"
                  value={emailDraft.body}
                  onChange={(e) => setEmailDraft((d) => ({ ...d, body: e.target.value }))}
                />
                <div style={{ marginTop: 10 }}>
                  <button style={s.btn('primary', saving)} onClick={handleSaveDraft} disabled={saving}>
                    {saving ? 'Saving…' : 'Save Draft'}
                  </button>
                </div>
              </div>
            </div>

            {/* ── Right column ────────────────────────────────────────── */}
            <div>
              <div style={s.card}>
                <div style={s.cardLabel}>Tender Matches ({matches.length})</div>
                {matches.length === 0 ? (
                  <p style={{ fontSize: 13, color: '#334155' }}>No matches found.</p>
                ) : matches.map((m) => (
                  <div key={m.id} style={s.matchCard}>
                    <div style={s.matchTitle}>{m.title || '—'}</div>
                    <div style={s.matchMeta}>
                      <span style={s.matchMetaItem}>{m.country || '—'}</span>
                      <span style={s.matchMetaItem}>{fmtDate(m.deadline)}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: scoreColor(m.score || 0) }}>
                        {m.score != null ? Math.round(m.score) : '—'}
                      </span>
                    </div>
                    {m.reasoning && <div style={s.matchReasoning}>{m.reasoning}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Sticky action bar ──────────────────────────────────────── */}
        <div style={s.actionBar}>
          <button style={s.btn('green', acting)} onClick={handleApprove} disabled={acting}>
            Approve &amp; Queue
          </button>
          <button style={s.btn('gray', acting)} onClick={handleSkip} disabled={acting}>
            Skip
          </button>
          {outreach?.status && (
            <span style={s.outreachStatusText}>
              Outreach: <span style={{ color: '#60a5fa' }}>{outreach.status}</span>
            </span>
          )}
        </div>
      </div>
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      {/* Title + stats */}
      <div style={s.titleRow}>
        <h1 style={s.heading}>Prospector</h1>
        <div style={s.statsRow}>
          {stats && [
            { label: 'Total',    val: stats.total },
            { label: 'Pending',  val: stats.pending },
            { label: 'Approved', val: stats.approved },
            { label: 'Skipped',  val: stats.skipped },
            { label: 'Flagged',  val: stats.flagged },
            { label: 'Sent today', val: `${stats.sent_today} / ${stats.daily_limit}` },
          ].map(({ label, val }) => (
            <div key={label} style={s.statPill}>
              <span style={s.statLabel}>{label}</span>
              <span style={s.statVal}>{val}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Action row */}
      <div style={s.actionRow}>
        <button
          style={s.btn(runForm.open ? 'ghost' : 'primary')}
          onClick={() => setRunForm((f) => ({ ...f, open: !f.open }))}
        >
          Run Domain
        </button>
        <button
          style={s.btn(discoverForm.open ? 'ghost' : 'primary')}
          onClick={() => setDiscoverForm((f) => ({ ...f, open: !f.open }))}
        >
          Discover
        </button>
        <button style={s.btn('ghost')} onClick={loadAll}>Refresh</button>
      </div>

      {/* Inline: Run Domain */}
      {runForm.open && (
        <form style={s.inlineForm} onSubmit={submitRun}>
          <div style={s.formGroup}>
            <label style={s.lbl}>Domain</label>
            <input
              style={s.input}
              placeholder="example.com"
              value={runForm.domain}
              onChange={(e) => setRunForm((f) => ({ ...f, domain: e.target.value }))}
              required
            />
          </div>
          <div style={s.checkRow}>
            <input
              type="checkbox"
              id="force"
              checked={runForm.force}
              onChange={(e) => setRunForm((f) => ({ ...f, force: e.target.checked }))}
            />
            <label htmlFor="force" style={s.checkLbl}>Force re-run</label>
          </div>
          <button type="submit" style={s.btn('primary')}>Start</button>
          <button type="button" style={s.btn('ghost')} onClick={() => setRunForm({ open: false, domain: '', force: false })}>
            Cancel
          </button>
        </form>
      )}

      {/* Inline: Discover */}
      {discoverForm.open && (
        <form style={s.inlineForm} onSubmit={submitDiscover}>
          <div style={s.formGroup}>
            <label style={s.lbl}>Search query</label>
            <input
              style={s.input}
              placeholder="e.g. construction companies Netherlands"
              value={discoverForm.query}
              onChange={(e) => setDiscoverForm((f) => ({ ...f, query: e.target.value }))}
              required
            />
          </div>
          <div style={s.formGroup}>
            <label style={s.lbl}>Limit</label>
            <input
              type="number" min="1" max="100"
              style={s.numInput}
              value={discoverForm.limit}
              onChange={(e) => setDiscoverForm((f) => ({ ...f, limit: e.target.value }))}
            />
          </div>
          <button type="submit" style={s.btn('primary')}>Start</button>
          <button type="button" style={s.btn('ghost')} onClick={() => setDiscoverForm({ open: false, query: '', limit: 20 })}>
            Cancel
          </button>
        </form>
      )}

      {/* Table */}
      {loading ? (
        <p style={{ color: '#334155', fontSize: 14 }}>Loading…</p>
      ) : (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Domain</th>
              <th style={s.th}>Company</th>
              <th style={s.th}>Country</th>
              <th style={s.th}>Sector</th>
              <th style={s.th}>Confidence</th>
              <th style={s.th}>Matches</th>
              <th style={s.th}>Status</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {prospects.length === 0 ? (
              <tr><td colSpan={8} style={s.emptyRow}>No prospects yet. Use Run Domain or Discover to add some.</td></tr>
            ) : prospects.map((p) => {
              const c = confPill(p.confidence);
              return (
                <tr key={p.id}>
                  <td style={s.tdPrimary}>{p.domain}</td>
                  <td style={s.td}>{p.company_name || '—'}</td>
                  <td style={s.td}>{p.country || '—'}</td>
                  <td style={s.td}>{p.sector || '—'}</td>
                  <td style={s.td}>
                    <span style={{ display: 'inline-block', background: c.bg, color: c.color, fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 6 }}>
                      {c.text}
                    </span>
                  </td>
                  <td style={s.td}>{p.match_count ?? '—'}</td>
                  <td style={s.td}>
                    <span style={statusBadge(p.status)}>{p.status?.replace('_', ' ') || '—'}</span>
                  </td>
                  <td style={s.td}>
                    <button style={s.reviewBtn} onClick={() => handleSelectProspect(p.id)}>
                      Review
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
